import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { DraftCardProps, DraftViewMode } from "./types";
import { DraftStatusBadge } from "./DraftStatusBadge";
import { DraftActionButtons } from "./DraftActionButtons";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Clock,
  User,
  Calendar,
  Edit,
  Eye,
  GitCommit,
  FileText,
  MessageSquare,
  Users,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

const getUserInitials = (name: string): string => {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const formatChangeCount = (changes: any[]): string => {
  const count = changes?.length || 0;
  if (count === 0) return "No changes";
  if (count === 1) return "1 change";
  return `${count} changes`;
};

export const DraftCard: React.FC<DraftCardProps> = ({
  draft,
  viewMode,
  isSelected,
  showActions = true,
  onSelect,
  onEdit,
  onPublish,
  onDelete,
  onReview,
  onPreview,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const userInitials = getUserInitials(draft.createdBy);
  const timeAgo = formatDistanceToNow(draft.updatedAt, { addSuffix: true });
  const createdTimeAgo = formatDistanceToNow(draft.createdAt, {
    addSuffix: true,
  });
  const changeCount = formatChangeCount(draft.changes);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        onSelect?.();
        break;
      case "e":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          onEdit?.();
        }
        break;
      case "p":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          onPreview?.();
        }
        break;
    }
  };

  const renderGridView = () => (
    <Card
      className={cn(
        "cursor-pointer transition-all duration-200 hover:shadow-md h-full",
        isSelected && "ring-2 ring-primary shadow-md",
        draft.status === "approved" && "border-green-500",
        draft.status === "rejected" && "border-red-500",
      )}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      role="button"
      aria-label={`Draft ${draft.name} - ${draft.status} - ${timeAgo}`}
      aria-selected={isSelected}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3
              className="font-semibold text-base mb-1 line-clamp-1"
              title={draft.name}
            >
              {draft.name}
            </h3>
            <div className="flex items-center space-x-2">
              <DraftStatusBadge status={draft.status} />
              {draft.isShared && (
                <Badge variant="outline" className="text-xs">
                  <Users className="w-3 h-3 mr-1" />
                  Shared
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 h-full flex flex-col">
        {/* Description */}
        {draft.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-3 flex-1">
            {draft.description}
          </p>
        )}

        {/* Metadata */}
        <div className="space-y-2 text-xs text-muted-foreground flex-shrink-0">
          <div className="flex items-center space-x-1">
            <Avatar className="w-4 h-4">
              <AvatarFallback className="text-xs">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <span className="truncate">{draft.createdBy}</span>
          </div>

          <div className="flex items-center space-x-1">
            <Edit className="w-3 h-3" />
            <span>Updated {timeAgo}</span>
          </div>

          <div className="flex items-center space-x-1">
            <Calendar className="w-3 h-3" />
            <span>Created {createdTimeAgo}</span>
          </div>

          {draft.changes && draft.changes.length > 0 && (
            <div className="flex items-center space-x-1">
              <GitCommit className="w-3 h-3" />
              <span>{changeCount}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        {showActions && (isHovered || isSelected) && (
          <div className="flex justify-center mt-3 pt-3 border-t">
            <DraftActionButtons
              draft={draft}
              showEdit={!!onEdit}
              showPublish={!!onPublish}
              showDelete={!!onDelete}
              showReview={!!onReview}
              showPreview={!!onPreview}
              onEdit={onEdit}
              onPublish={onPublish}
              onDelete={onDelete}
              onReview={onReview}
              onPreview={onPreview}
              size="sm"
              variant="dropdown"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderListView = () => (
    <Card
      className={cn(
        "cursor-pointer transition-all duration-200 hover:shadow-md",
        isSelected && "ring-2 ring-primary shadow-md",
        draft.status === "approved" && "border-green-500",
        draft.status === "rejected" && "border-red-500",
      )}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      role="button"
      aria-label={`Draft ${draft.name} - ${draft.status} - ${timeAgo}`}
      aria-selected={isSelected}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1 min-w-0">
            {/* Avatar */}
            <Avatar className="w-10 h-10 flex-shrink-0">
              <AvatarFallback>{userInitials}</AvatarFallback>
            </Avatar>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="font-semibold text-base truncate">
                  {draft.name}
                </h3>
                <DraftStatusBadge status={draft.status} />
                {draft.isShared && (
                  <Badge variant="outline" className="text-xs">
                    <Users className="w-3 h-3 mr-1" />
                    Shared
                  </Badge>
                )}
              </div>

              {draft.description && (
                <p className="text-sm text-muted-foreground mb-2 line-clamp-1">
                  {draft.description}
                </p>
              )}

              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <User className="w-3 h-3" />
                  <span>{draft.createdBy}</span>
                </div>

                <div className="flex items-center space-x-1">
                  <Edit className="w-3 h-3" />
                  <span>Updated {timeAgo}</span>
                </div>

                {draft.changes && draft.changes.length > 0 && (
                  <div className="flex items-center space-x-1">
                    <GitCommit className="w-3 h-3" />
                    <span>{changeCount}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          {showActions && (
            <div className="flex-shrink-0 ml-4">
              <DraftActionButtons
                draft={draft}
                showEdit={!!onEdit}
                showPublish={!!onPublish}
                showDelete={!!onDelete}
                showReview={!!onReview}
                showPreview={!!onPreview}
                onEdit={onEdit}
                onPublish={onPublish}
                onDelete={onDelete}
                onReview={onReview}
                onPreview={onPreview}
                size="sm"
                variant={isHovered || isSelected ? "inline" : "dropdown"}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderTableView = () => (
    <tr
      className={cn(
        "cursor-pointer transition-all duration-200 hover:bg-muted/50 border-b",
        isSelected && "bg-primary/5",
      )}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      role="button"
      aria-label={`Draft ${draft.name} - ${draft.status} - ${timeAgo}`}
      aria-selected={isSelected}
    >
      {/* Name */}
      <td className="px-4 py-3">
        <div className="flex items-center space-x-2">
          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-medium truncate">{draft.name}</div>
            {draft.description && (
              <div className="text-sm text-muted-foreground truncate">
                {draft.description}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <div className="flex items-center space-x-2">
          <DraftStatusBadge status={draft.status} />
          {draft.isShared && (
            <Badge variant="outline" className="text-xs">
              <Users className="w-3 h-3" />
            </Badge>
          )}
        </div>
      </td>

      {/* Author */}
      <td className="px-4 py-3">
        <div className="flex items-center space-x-2">
          <Avatar className="w-6 h-6">
            <AvatarFallback className="text-xs">{userInitials}</AvatarFallback>
          </Avatar>
          <span className="text-sm">{draft.createdBy}</span>
        </div>
      </td>

      {/* Updated */}
      <td className="px-4 py-3 text-sm text-muted-foreground">
        <div className="flex flex-col">
          <span>{format(draft.updatedAt, "MMM d, yyyy")}</span>
          <span className="text-xs">{timeAgo}</span>
        </div>
      </td>

      {/* Changes */}
      <td className="px-4 py-3 text-sm text-muted-foreground">
        <div className="flex items-center space-x-1">
          <GitCommit className="w-3 h-3" />
          <span>{draft.changes?.length || 0}</span>
        </div>
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        {showActions && (
          <DraftActionButtons
            draft={draft}
            showEdit={!!onEdit}
            showPublish={!!onPublish}
            showDelete={!!onDelete}
            showReview={!!onReview}
            showPreview={!!onPreview}
            onEdit={onEdit}
            onPublish={onPublish}
            onDelete={onDelete}
            onReview={onReview}
            onPreview={onPreview}
            size="sm"
            variant="dropdown"
          />
        )}
      </td>
    </tr>
  );

  // Render based on view mode
  switch (viewMode) {
    case "grid":
      return renderGridView();
    case "list":
      return renderListView();
    case "table":
      return renderTableView();
    default:
      return renderListView();
  }
};

export default DraftCard;
